/* eslint-disable no-unused-vars */
import { useState, useEffect } from 'react'
import { getBrands, getUserSizes } from '../api'
import { useAuth } from '../context/AuthContext'
import { useProfiles } from '../context/ProfileContext'
import BrandCard from '../components/BrandCard'
import SizeModal from '../components/SizeModal'
import './MyBrands.css'

function StarRating({ brandId, rating, onRate }) {
    const [hovered, setHovered] = useState(0)
    return (
        <div className="star-rating">
            {[1, 2, 3, 4, 5].map(star => (
                <button
                    key={star}
                    className={`star-btn ${(hovered || rating) >= star ? 'filled' : ''}`}
                    onMouseEnter={() => setHovered(star)}
                    onMouseLeave={() => setHovered(0)}
                    onClick={() => onRate(rating === star ? null : star)}
                    aria-label={`Rate ${star} star${star !== 1 ? 's' : ''}`}
                >
                    ★
                </button>
            ))}
        </div>
    )
}

function MyBrands() {
    const { user } = useAuth()
    const { activeProfile, likedBrandIds, dislikedBrandIds, getBrandRating, setRating } = useProfiles()
    const [allBrands, setAllBrands] = useState([])
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState('saved')
    const [sortBy, setSortBy] = useState('date')
    const [userSizes, setUserSizes] = useState({})
    const [sizingBrand, setSizingBrand] = useState(null)

    useEffect(() => {
        getBrands().then(data => {
            setAllBrands(data || [])
            setLoading(false)
        })
    }, [])

    useEffect(() => {
        if (user) {
            getUserSizes(user.id).then(sizes => {
                const byBrand = {}
                sizes.forEach(s => { byBrand[s.brand_id] = s })
                setUserSizes(byBrand)
            })
        }
    }, [user])

    const handleSizeSaved = () => {
        if (user) {
            getUserSizes(user.id).then(sizes => {
                const byBrand = {}
                sizes.forEach(s => { byBrand[s.brand_id] = s })
                setUserSizes(byBrand)
            })
        }
    }

    if (!user) {
        return (
            <div className="my-brands-page">
                <div className="empty-state">
                    <h2>Sign in to see your brands</h2>
                    <p>Save brands you discover and build your own collection. Sign in to get started.</p>
                </div>
            </div>
        )
    }

    const savedBrands = allBrands.filter(b => likedBrandIds.includes(b.id))
    const dislikedBrands = allBrands.filter(b => dislikedBrandIds.includes(b.id))
    const profileName = activeProfile?.is_default ? 'My Brands' : activeProfile?.name || 'My Brands'

    const sortBrands = (brands) => {
        if (sortBy === 'rating-high') {
            const rated = brands.filter(b => getBrandRating(b.id) != null)
                .sort((a, b) => getBrandRating(b.id) - getBrandRating(a.id))
            const unrated = brands.filter(b => getBrandRating(b.id) == null)
            return { rated, unrated, showSections: true }
        }
        if (sortBy === 'rating-low') {
            const rated = brands.filter(b => getBrandRating(b.id) != null)
                .sort((a, b) => getBrandRating(a.id) - getBrandRating(b.id))
            const unrated = brands.filter(b => getBrandRating(b.id) == null)
            return { rated, unrated, showSections: true }
        }
        return { rated: brands, unrated: [], showSections: false }
    }

    const renderBrandList = (brands, showSizingBtn = false) => {
        const { rated, unrated, showSections } = sortBrands(brands)

        const renderCard = (brand) => (
            <div key={brand.id} className="my-brands-card-wrapper">
                <BrandCard brand={brand} showSimilar={true} />
                <div className="my-brands-card-footer">
                    <StarRating
                        brandId={brand.id}
                        rating={getBrandRating(brand.id)}
                        onRate={(r) => setRating(brand.id, r)}
                    />
                    {showSizingBtn && (
                        <button
                            className={`sizing-btn ${userSizes[brand.id] ? 'has-sizing' : ''}`}
                            onClick={() => setSizingBrand(brand)}
                        >
                            {userSizes[brand.id] ? '✓ Sizing' : '+ Sizing'}
                        </button>
                    )}
                </div>
            </div>
        )

        if (!showSections) {
            return <div className="brand-grid">{rated.map(renderCard)}</div>
        }

        return (
            <>
                {rated.length > 0 && (
                    <div className="brand-grid">{rated.map(renderCard)}</div>
                )}
                {unrated.length > 0 && (
                    <>
                        {rated.length > 0 && <p className="unrated-label">Unrated</p>}
                        <div className="brand-grid">{unrated.map(renderCard)}</div>
                    </>
                )}
            </>
        )
    }

    return (
        <div className="my-brands-page">
            <div className="page-header">
                <h1>{profileName}</h1>
            </div>

            <div className="my-brands-controls">
                <div className="my-brands-tabs">
                    <button
                        className={`my-brands-tab ${activeTab === 'saved' ? 'active' : ''}`}
                        onClick={() => setActiveTab('saved')}
                    >
                        Saved ({savedBrands.length})
                    </button>
                    <button
                        className={`my-brands-tab ${activeTab === 'disliked' ? 'active' : ''}`}
                        onClick={() => setActiveTab('disliked')}
                    >
                        Not for me ({dislikedBrands.length})
                    </button>
                </div>
                <select
                    className="sort-select"
                    value={sortBy}
                    onChange={e => setSortBy(e.target.value)}
                >
                    <option value="date">Date saved</option>
                    <option value="rating-high">Rating: high to low</option>
                    <option value="rating-low">Rating: low to high</option>
                </select>
            </div>

            {loading ? (
                <div className="loading">Loading your brands...</div>
            ) : (activeTab === 'saved' ? savedBrands : dislikedBrands).length === 0 ? (
                <div className="empty-state">
                    {activeTab === 'saved' ? (
                        <>
                            <h2>No saved brands yet</h2>
                            <p>Head to Discover to find brands that match your taste.</p>
                        </>
                    ) : (
                        <>
                            <h2>No disliked brands</h2>
                            <p>Brands you mark as "Not for me" will appear here.</p>
                        </>
                    )}
                </div>
            ) : renderBrandList(
                activeTab === 'saved' ? savedBrands : dislikedBrands,
                activeTab === 'saved'
            )}

            {sizingBrand && (
                <SizeModal
                    brand={sizingBrand}
                    existingSize={userSizes[sizingBrand.id]}
                    onClose={() => setSizingBrand(null)}
                    onSaved={handleSizeSaved}
                />
            )}
        </div>
    )
}

export default MyBrands